import { inject, Injectable } from '@angular/core';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { SchoolControllerApiService } from '@lx/shared/api';
import { Course, PredefinedOnlineSelections, PredefinedOnlineSelectionsWithCourses } from '@lx/shared/data-access/types';
import { getLocale } from '@lx/shared/i18n';
import { getCompanyHeader } from '@lx/shared/tokens';
import { Memoize } from '@lx/shared/utils-http';
import { mergePredefinedOnlineSelectionsWithCourses } from './utils/merge-course-options.util';

/**
 * Service to manage online courses.
 *
 * This service provides methods to fetch online courses and their predefined selections
 * from the backend API. It's designed to be used by both QT Lite and Online QT applications.
 *
 * **Available methods:**
 * - `getOnlineCourses()` - Fetch courses only
 * - `getPredefinedOnlineSelections()` - Fetch predefined selections only
 * - `getCourseOptions()` - Fetch both and merge automatically (convenience method)
 */
@Injectable({
  providedIn: 'root',
})
export class OnlineCourseService {
  private readonly locale = getLocale();
  private readonly companyHeader = getCompanyHeader();
  private readonly schoolService = inject(SchoolControllerApiService);

  /**
   * Get predefined online selections for a school.
   *
   * Predefined selections include pre-configured course packages with specific
   * durations and start/end dates.
   *
   * @param schoolCode The code of the school to get the selection from
   * @returns Observable of predefined online selections
   */
  @Memoize({ tags: ['getPredefinedOnlineSelections'], hashFunction: (schoolCode: string) => schoolCode })
  getPredefinedOnlineSelections(schoolCode: string): Observable<PredefinedOnlineSelections[]> {
    if (!schoolCode) {
      return of([]);
    }
    return this.schoolService.findPredefinedOnlineSelections(schoolCode, this.locale, this.companyHeader);
  }

  /**
   * Get online courses for a school.
   *
   * Returns only courses that have lesson information (filters out courses without lessons).
   *
   * @param schoolCode The code of the school to get the courses from
   * @returns Observable of online courses
   */
  @Memoize({ tags: ['getOnlineCourses'], hashFunction: (schoolCode: string) => schoolCode })
  getOnlineCourses(schoolCode: string): Observable<Course[]> {
    if (!schoolCode) {
      return of([]);
    }
    return this.schoolService.findSchoolCourses(schoolCode, this.locale, this.companyHeader).pipe(
      switchMap((courses) => {
        if (!courses || courses.length === 0) {
          return of([]);
        }
        return of(courses.filter((course) => course?.lesson));
      }),
    );
  }

  /**
   * Get merged course options (courses + predefined selections).
   *
   * This is a convenience method that fetches both courses and predefined selections,
   * then merges them into a single data structure ready for display. It leverages
   * the memoization of the individual methods for optimal performance.
   *
   * **Use this when:**
   * - You only need the merged result for display
   * - You want a simpler API (one call instead of two)
   * - You're using observables/subscriptions
   *
   * **Use separate methods when:**
   * - You need courses and selections separately (e.g., for multiple signals)
   * - You need more granular control over data loading
   * - You're using computed signals (use utility function instead)
   *
   * @param schoolCode The code of the school to get the course options from
   * @returns Observable of merged course options sorted by weight
   *
   * @example
   * // In a component:
   * this.onlineCourseService.getCourseOptions(schoolCode)
   *   .pipe(takeUntilDestroyed(this.destroyRef))
   *   .subscribe(courseOptions => {
   *     this.courseOptions.set(courseOptions);
   *   });
   */
  getCourseOptions(schoolCode: string): Observable<PredefinedOnlineSelectionsWithCourses[]> {
    return combineLatest([this.getOnlineCourses(schoolCode), this.getPredefinedOnlineSelections(schoolCode)]).pipe(
      map(([courses, predefinedSelections]: [Course[], PredefinedOnlineSelections[]]) =>
        mergePredefinedOnlineSelectionsWithCourses(predefinedSelections, courses),
      ),
    );
  }
}
